import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { makeStyles } from '@material-ui/core';
import {
  BaseButton,
  FormikField,
  BaseCardField,
  BaseInputLabel,
} from 'src/components/common';
import { useFormikErrorsHandler } from 'src/utils/hooks/useFormikErrorsHandler';
import { newContactValidationSchema } from './newContactValidationSchema';
import { ExpertisesMultiSelect } from 'src/components/fields/selects/ExpertisesMultiSelect';
import { AvailableForCheckboxesContainer } from '../../../../components/fields/other/AvailableForCheckboxes/AvailableForCheckboxesContainer';
import { CountriesMultiSelect } from 'src/components/fields/selects/CountriesMultiSelect';
import { Rating } from '@material-ui/lab';
import { ICreateGuestModel, IGuestInfo } from 'src/api/base/Guest/types';
import { IBaseForm } from 'src/utils/types/forms';
import { BaseButtonGroup } from '../../../../components/common/BaseButton/BaseButtonGroup';
import { FilesUploader } from 'src/components/common/FilesUploader';
import { BaseHelperText } from '../../../../components/common/BaseTextField/BaseHelperText/BaseHelperText';
import { capitalizeFirstLetter } from 'src/utils/helpers';
import { GenderSelect } from '../../../../components/fields/selects/GenderSelect';

const useStyles = makeStyles((theme) => ({
  formGroups: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: '20px',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  formGroup: {
    display: 'grid',
    gridGap: '20px',
    gridAutoRows: 'max-content',
  },
  submitSection: {
    marginTop: 'auto',
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(100px, 200px)',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
      gridGap: '20px',
    },
  },
  available: {
    padding: '23px',
  },
}));

type PNewContactForm = IBaseForm<ICreateGuestModel> & {
  guest?: IGuestInfo | null;
  isCreatingForm?: boolean;
  submitBtnText?: string;
};

const formInitialValues = {
  first_name: '',
  last_name: '',
  gender: '',
  title: '',
  company_name: '',
  linkedin: '',
  expertise: [],
  available: [],
  country: [],
  description: '',
  rating: 4,
  is_company_logo_visible: false,
  is_company_description_visible: false,

  guest_company_description: '',
  guest_company_logo: '',
  manager_company_description: '',
  manager_company_logo: '',
};

const formKeys = Object.keys(formInitialValues);

export const NewContactForm: React.FC<PNewContactForm> = (props) => {
  const {
    onSubmit,
    loading,
    errors,
    guest,
    isCreatingForm,
    submitBtnText = 'Save',
  } = props;

  const classes = useStyles();
  const formik = useFormik<ICreateGuestModel>({
    initialValues: { ...formInitialValues },
    validationSchema: newContactValidationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      const localGuest = guest || {};
      onSubmit({ ...localGuest, ...values });
      formik.setErrors({});
    },
  });
  const { setFieldValue } = formik;

  useEffect(() => {
    if (guest) {
      const arrKeys = ['expertise', 'available', 'country'];
      formKeys.forEach((key) => {
        if (!isCreatingForm && arrKeys.includes(key)) {
          // @ts-ignore
          const val = guest[key].map((el: any) => el.id);
          setFieldValue(key, val);
        } else if (key === 'guest_company_description') {
          setFieldValue(key, guest[key] || '');
        } else {
          // @ts-ignore
          setFieldValue(key, guest[key]);
        }
      });
    }
  }, [guest, isCreatingForm, setFieldValue]);

  const { setFieldError } = formik;

  useFormikErrorsHandler({ errors, setFieldError });

  const [hover, setHover] = React.useState<number>(5);

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <div className={classes.formGroups}>
          <div className={classes.formGroup}>
            <FormikField
              label="First name*"
              name="first_name"
              formik={formik}
            />
            <FormikField label="Last name*" name="last_name" formik={formik} />
            <FormikField
              label="Gender*"
              name="gender"
              formik={formik}
              inputComponent={GenderSelect}
              onChange={(newValue: string) => {
                formik.setFieldValue('gender', newValue);
              }}
            />
            <FormikField label="Title*" name="title" formik={formik} />
            <FormikField
              label="Company name*"
              name="company_name"
              formik={formik}
            />
            <FormikField
              label="LinkedIn link*"
              name="linkedin"
              formik={formik}
            />
            <FormikField
              name="expertise"
              formik={formik}
              label="Areas of expertise *"
              inputComponent={ExpertisesMultiSelect}
              onChange={(value: any) => {
                formik.setFieldValue('expertise', value);
              }}
            />

            <BaseButtonGroup
              value={formik.values.is_company_logo_visible}
              label="Use company logo?"
              onChange={(value) => {
                formik.setFieldValue('is_company_logo_visible', value);
              }}
            />

            {formik.values.is_company_logo_visible && (
              <>
                <div>
                  <BaseInputLabel>Company logo*</BaseInputLabel>
                  <FilesUploader
                    accept="image/*"
                    onChooseFile={(files) => {
                      formik.setFieldValue(
                        'guest_company_logo',
                        files.length ? files[0] : '',
                      );
                    }}
                    files={formik.values.guest_company_logo}
                    multiple={false}
                  />
                  {formik.errors.guest_company_logo && (
                    <BaseHelperText error>
                      {capitalizeFirstLetter(
                        formik.errors.guest_company_logo || '',
                      )}
                    </BaseHelperText>
                  )}
                </div>
              </>
            )}

            <BaseButtonGroup
              value={formik.values.is_company_description_visible}
              label="Use company description?"
              onChange={(value) => {
                formik.setFieldValue('is_company_description_visible', value);
              }}
            />

            {formik.values.is_company_description_visible && (
              <FormikField
                label="Company Description*"
                name="guest_company_description"
                formik={formik}
                multiline
                rowsMin={6}
                rows={6}
                InputProps={{
                  style: {
                    height: '136px',
                  },
                }}
              />
            )}
          </div>

          <div className={classes.formGroup}>
            <AvailableForCheckboxesContainer
              value={formik.values.available}
              errorText={formik.errors.available}
              onChange={(value: any) => {
                formik.setFieldValue('available', value);
              }}
              offCardStyles={false}
              className={classes.available}
            />
            <FormikField
              inputComponent={CountriesMultiSelect}
              name="country"
              formik={formik}
              label="Country of residence*"
              onChange={(value: any) => {
                formik.setFieldValue('country', value);
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <p>Description*</p>
              <p>
                Characters remaining:{' '}
                {510 - formik.values?.description?.length < 0
                  ? '0'
                  : 510 - formik.values?.description?.length}
              </p>
            </div>
            <FormikField
              multiline
              rowsMin={6}
              rows={6}
              name="description"
              formik={formik}
              InputProps={{
                style: {
                  height: '136px',
                },
              }}
            />

            <div className={classes.submitSection}>
              <BaseCardField
                label="Rating"
                errorText={formik.errors.rating}
                cardOptions={{ offCardStyles: true }}>
                <Rating
                  name="rating"
                  size="large"
                  value={formik.values.rating}
                  precision={0.5}
                  onChangeActive={(_, newHover) => {
                    setHover(newHover);
                  }}
                  onClick={() => {
                    formik.setFieldValue('rating', hover);
                  }}
                />
              </BaseCardField>

              <BaseButton variant="contained" type="submit" loading={loading}>
                {submitBtnText}
              </BaseButton>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
